import { AlertTriangle, ArrowLeftRight } from 'lucide-react';
import {
  useWatch,
  type Control,
  type FieldErrors,
  type Path,
  type UseFormRegister,
  type UseFormReturn,
} from 'react-hook-form';

import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { FieldShell, errorMessage } from './form-helpers';
import type { ProvisionalSubmitValues, ReclassifyTradeType } from './provisional-invoice-schema';
import type { InvoiceRow } from '../types';

// Edit-mode adapter: renders the reclassify card only for an existing invoice,
// pulling register/control/errors off the form so call sites stay one element.
export function EditReclassifyField<TValues extends ProvisionalSubmitValues>(props: {
  readonly form: UseFormReturn<TValues>;
  readonly existingInvoice: InvoiceRow | null;
  readonly disabled: boolean;
  readonly currentType: ReclassifyTradeType;
}) {
  if (props.existingInvoice === null) return null;
  return (
    <TradeInvoiceReclassifyField
      register={props.form.register}
      control={props.form.control}
      errors={props.form.formState.errors}
      disabled={props.disabled}
      currentType={props.currentType}
    />
  );
}

interface ReclassifyFieldProps<TValues extends ProvisionalSubmitValues> {
  readonly register: UseFormRegister<TValues>;
  readonly control: Control<TValues>;
  readonly errors: FieldErrors<TValues>;
  readonly disabled: boolean;
  readonly currentType: ReclassifyTradeType;
}

// Edit-only control that re-types a Draft trade invoice between Provisional and
// Final Settlement. The reason + warning only surface once the selected target
// differs from the current type, matching the launchpad reclassify card.
export function TradeInvoiceReclassifyField<TValues extends ProvisionalSubmitValues>(
  props: ReclassifyFieldProps<TValues>,
) {
  const { register, control, errors, disabled, currentType } = props;
  const target = useWatch({
    control,
    name: 'reclassify_to_invoice_type' as Path<TValues>,
  }) as ReclassifyTradeType | undefined;
  const changed = target !== undefined && target !== currentType;
  return (
    <div
      className="border-border bg-muted/30 space-y-3 rounded-lg border border-dashed p-3"
      data-testid="reclassify-invoice-type"
    >
      <div className="flex items-center gap-2">
        <ArrowLeftRight className="text-muted-foreground size-4" aria-hidden />
        <span className="text-sm font-medium">Reclassify Invoice Type</span>
      </div>
      <p className="text-muted-foreground text-xs">
        Current type: <span className="text-foreground font-semibold">{currentType}</span>
      </p>
      <FieldShell
        id="reclassify_to_invoice_type"
        label="Reclassify to"
        error={errorMessage(errors, 'reclassify_to_invoice_type')}
      >
        <Select
          id="reclassify_to_invoice_type"
          disabled={disabled}
          {...register('reclassify_to_invoice_type' as Path<TValues>)}
        >
          <option value="Provisional">Provisional</option>
          <option value="Final Settlement">Final</option>
        </Select>
      </FieldShell>
      {changed && (
        <ReclassifyReason
          register={register}
          error={errorMessage(errors, 'reclassification_reason')}
          disabled={disabled}
        />
      )}
    </div>
  );
}

function ReclassifyReason<TValues extends ProvisionalSubmitValues>(props: {
  readonly register: UseFormRegister<TValues>;
  readonly error: string | undefined;
  readonly disabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-1">
        <Label htmlFor="reclassification_reason">
          Reason for reclassification <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="reclassification_reason"
          rows={2}
          disabled={props.disabled}
          placeholder="e.g. Incorrectly marked as Provisional, should be Final invoice"
          aria-invalid={props.error !== undefined}
          {...props.register('reclassification_reason' as Path<TValues>)}
        />
        {props.error !== undefined && (
          <p className="text-destructive text-sm" role="alert">
            {props.error}
          </p>
        )}
      </div>
      <p className="flex items-center gap-1 text-xs text-amber-600">
        <AlertTriangle className="size-3" aria-hidden />
        This will change the invoice classification and update linked payment types.
      </p>
    </div>
  );
}
